import { useMemo } from 'react';
import { isBrowser } from '@noths/polaris-client-utils';
import type { DeepPartial, Dictionary } from '@noths/polaris-dev-ts-recipes';
import type { Middleware, PreloadedState, Reducer } from '@reduxjs/toolkit';
import { configureStore } from '@reduxjs/toolkit';
import { createRouterMiddleware, initialRouterState } from 'connected-next-router';
import { format } from 'url';

import { routerBuilder } from './builders/router';
import { hydrateState } from './stateHydration';
import type { AppProps, Router, StateWithRouter } from './types';

type StoreState<T> = T | ((T & StateWithRouter) | StateWithRouter);
type MiddlewareWithState<T> = Middleware<StoreState<T>>;

type Options<T> = {
  middleware?: MiddlewareWithState<T>[];
  reducer: Reducer;
};

const routerMiddleware = createRouterMiddleware();

const getRouterInitialState = (router: Partial<Router> = {}) => {
  const { asPath, pathname, query } = { ...routerBuilder(), ...router };
  // Prefer the resolved URL (Next's `asPath`) so dynamic/catch-all routes seed
  // the real path, not the route pattern (`/shop/[[...slug]]`). Falls back to
  // pathname+query for callers (e.g. SSR getStore) that pass an already-resolved
  // pathname and no asPath.
  const url = asPath ?? format({ pathname, query });
  return initialRouterState(url);
};

export const generateStore = <TState extends PreloadedState<Dictionary>>({
  middleware = [],
  reducer,
}: Options<TState>) => {
  /*
      Initialise a store using the provided preloaded state, reducer, app props and middleware.
      Augment it with Next.js routing.
  */
  const initStore = (appProps?: AppProps, preloadedState?: DeepPartial<TState>) => {
    return configureStore({
      reducer,
      middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware().concat(...middleware, routerMiddleware),
      preloadedState: {
        ...preloadedState,
        router: getRouterInitialState(appProps?.router),
      },
    });
  };

  type AugmentedStore = Omit<ReturnType<typeof initStore>, 'getState'> & {
    getState: () => TState;
  };

  let store: AugmentedStore;

  /**
   * Retrieve the store instance, this can be used in class components and
   * static methods such as getServerSideProps
   * @param appProps - The Next.js application props.
   * @param preloadedState - The state to preload the store with.
   */
  const getStore = (appProps?: AppProps, preloadedState?: DeepPartial<TState>) => {
    const _store = store || initStore(appProps, preloadedState);

    // After navigating to a page with preloadedState, dispatch an action to hydrate the state
    if (preloadedState && store) {
      store.dispatch(hydrateState<DeepPartial<TState>>(preloadedState));
    }

    // For server always create a new store
    if (!isBrowser()) {
      return _store;
    }

    // set the store on the client
    if (!store) {
      store = _store;
    }

    return store;
  };

  /**
   * Retrieve the store instance, this can be used in functional components
   * static methods such as getServerSideProps
   * @param appProps - The Next.js application props.
   * @param preloadedState - The state to preload the store with.
   *
   */
  const useStore = (appProps?: AppProps, initialState?: TState) =>
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useMemo(() => getStore(appProps, initialState), [initialState]);

  return { useStore, getStore };
};
